import {
  ApolloClient,
  DataProxy,
  NormalizedCacheObject,
  useLazyQuery,
  useMutation
} from '@apollo/client';
import {
  AssignMarkerData,
  AssignMarkerVars,
  UnassignMarkerData,
  UnassignMarkerVars,
  listAssessmentCourseData,
  listAssessmentCourseVars,
  submittedLessonsData,
  submittedLessonsVar,
  submittedPortfoliosData
} from '../gql-types';

import ApolloClientService from '../../shared/services/ApolloClientService';
import Config from '../../shared/config';
import { ExecutionResult } from 'graphql';
import GraphQL from '../gql';
import LessonPortfolioService from '../services/LessonPortfolioService';

let AssessmentApolloClient: ApolloClient<NormalizedCacheObject> = null;

export function useAssessments() {
  if (!AssessmentApolloClient) {
    AssessmentApolloClient = ApolloClientService.create(Config.ASSESSMENT_GQL);
  }

  const [
    listSubmittedPortfolios,
    { loading: portfoliosLoading, error: portfoliosError, data: portfoliosData }
  ] = useLazyQuery<submittedPortfoliosData>(
    GraphQL.Query.LIST_SUBMITTED_PORTFOLIOS,
    {
      client: AssessmentApolloClient,
      fetchPolicy: 'network-only'
    }
  );

  const [
    _listAssessmentCourses,
    {
      loading: listCoursesLoading,
      error: listCoursesError,
      data: listCoursesData
    }
  ] = useLazyQuery<listAssessmentCourseData, listAssessmentCourseVars>(
    GraphQL.Query.LIST_ASSESSMENT_COURSES,
    {
      client: AssessmentApolloClient,
      fetchPolicy: 'network-only'
    }
  );

  const [_listSubmittedLessons, { loading: lessonsLoading, error, data }] =
    useLazyQuery<submittedLessonsData, submittedLessonsVar>(
      GraphQL.Query.LIST_SUBMITTED_LESSONS,
      {
        client: AssessmentApolloClient
      }
    );

  const [
    _assignMarker,
    { loading: assignLoading, error: assignError, data: assignMarkerData }
  ] = useMutation<AssignMarkerData, AssignMarkerVars>(
    GraphQL.Mutation.ASSIGN_MARKER,
    {
      client: AssessmentApolloClient,
      update: (
        cache: DataProxy,
        { data }: ExecutionResult<AssignMarkerData>
      ) => {
        LessonPortfolioService.updateMutation(cache, data.assignMarker);
      }
    }
  );

  const [
    _unassignMarker,
    { loading: unassignLoading, error: unassignError, data: unassignData }
  ] = useMutation<UnassignMarkerData, UnassignMarkerVars>(
    GraphQL.Mutation.UNASSIGN_MARKERS,
    {
      client: AssessmentApolloClient,
      update: (
        cache: DataProxy,
        { data }: ExecutionResult<UnassignMarkerData>
      ) => {
        LessonPortfolioService.updateMutation(cache, data.unassignMarkers);
      }
    }
  );

  function listAssessmentCourses(groupkey: number) {
    _listAssessmentCourses({
      variables: { groupkey: groupkey }
    });
  }

  function listSubmittedLessons(groupkey: number, coursekey: number) {
    _listSubmittedLessons({
      variables: {
        groupkey: groupkey,
        coursekey: coursekey
      }
    });
  }

  function assignMarker(
    userkey: number,
    modulekey: number,
    layer: number,
    memberkey: number
  ) {
    _assignMarker({
      variables: {
        userkey,
        modulekey,
        layer,
        memberkey
      }
    });
  }

  function unassignMarker(userkey: number, modulekey: number, layer: number) {
    _unassignMarker({
      variables: {
        userkey,
        modulekey,
        layer
      }
    });
  }

  return {
    listCoursesLoading,
    listAssessmentCourses,
    listCoursesData: listCoursesData?.listGroupAssessmentCourses,
    lessonsLoading,
    listSubmittedLessons,
    submittedLessons: data?.submittedLessons,
    listSubmittedPortfolios,
    portfoliosLoading,
    portfoliosError,
    submittedPortfolios: portfoliosData?.submittedPortfolios,
    assignLoading,
    assignMarker,
    assignMarkerData: assignMarkerData?.assignMarker,
    unassignLoading,
    unassignMarker
  };
}
